<template>
  <!-- Replacement Quote Data Sheet page: Order Details on top, the 4-step data sheet next to Consumer Details + Internal Notes
       (same cards as the RQ detail page; Android FragmentDataSheet1…4) -->
  <div class="w-full font-roboto">
    <div v-if="order"
      class="w-full relative border-1 rounded-2xl px-[10px] laptop:px-[20px] pb-[10px] laptop:pb-[25px] largeDesktop:px-[30px] largeDesktop:pb-[35px] pt-[35px] border-gray-300">
      <DetailBackButton :fallback="detailPath" />
      <!-- breadcrumb bar, same as the S&R evaluation pages (EvalShell); no header breadcrumb on this page -->
      <div
        class="flex flex-wrap items-center gap-x-[6px] gap-y-1 min-h-[40px] mb-[15px] px-[16px] py-[8px] rounded-[8px] bg-white shadow-[0px_0px_10px_0px_#0000001A] text-[13px] font-roboto">
        <template v-for="(crumb, index) in crumbs" :key="crumb.label">
          <span v-if="index > 0" class="text-[#CCCCCC]">|</span>
          <!-- back to that page, never a new history entry; Ctrl/Cmd/middle click still opens a tab -->
          <a v-if="crumb.to" :href="$router.resolve(crumb.to).href" class="no-underline text-[#8479E1]"
            @click="openCrumb($event, crumb.to)">{{ crumb.label }}</a>
          <span v-else class="text-[#000000]">{{ crumb.label }}</span>
        </template>
      </div>
      <div class="flex flex-col gap-[20px]">
        <RqOrderDetailsCard :order="order" />
        <div class="flex mobile:flex-col laptop:flex-row gap-[20px] items-start">
          <div class="flex-1 min-w-0 w-full">
            <RqDataSheetForm :order-id="orderId" :order="order" :read-only="readOnly" @submitted="toDetail"
              @cancel="toDetail" />
          </div>
          <div class="w-full laptop:w-[320px] largeDesktop:w-[350px] laptop:flex-shrink-0 flex flex-col gap-[20px]">
            <RqConsumerCard :order="order" />
            <RqNotesCard :order-id="orderId" :can-write="canWriteNotes" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useToast } from "vue-toastification";
import DetailBackButton from "@/components/partialComponent/DetailBackButton.vue";
import RqOrderDetailsCard from "../../components/replacementQuote/RqOrderDetailsCard.vue";
import RqConsumerCard from "../../components/replacementQuote/RqConsumerCard.vue";
import RqNotesCard from "../../components/replacementQuote/RqNotesCard.vue";
import RqDataSheetForm from "../../components/replacementQuote/RqDataSheetForm.vue";
import { rqApi, RQ_STATUS } from "../../services/rqApi";
import { isSilentLogin } from "@/helpers/embedMode";
import { isNewTabClick } from "@/helpers/newTabClick";
import { goBackTo } from "@/helpers/historyPositions";
const toast = useToast();

export default {
  components: { DetailBackButton, RqOrderDetailsCard, RqConsumerCard, RqNotesCard, RqDataSheetForm },
  data() {
    return {
      order: null,
      myUserId: JSON.parse(localStorage.getItem("user") || "{}")?.user?.user_id,
    };
  },
  computed: {
    orderId() {
      return this.$route.params.id;
    },
    detailPath() {
      return `/replacement-quote/detail/${this.orderId}`;
    },
    // the list logs a silent login / SSO user out (router): no list crumb there
    crumbs() {
      return [
        ...(isSilentLogin() ? [] : [{ label: "Replacement Quote History", to: "/work-order/replacement-quote" }]),
        { label: "Order Details", to: this.detailPath },
        { label: "Data Sheet" },
      ];
    },
    // same rules as RqDetail: the tech on the order may edit, others only view
    readOnly() {
      const o = this.order || {};
      return ![o.technician?.user_id, o.seller_id, o.installer_id].map(String).includes(String(this.myUserId));
    },
    canWriteNotes() {
      const s = Number(this.order?.status);
      return !this.readOnly && s >= RQ_STATUS.INSTALLATION_PENDING && s < RQ_STATUS.CLOSED;
    },
  },
  watch: {
    orderId: { immediate: true, handler() { this.load(); } },
  },
  methods: {
    async load() {
      this.order = null;
      const res = await rqApi.detail(this.orderId);
      if (!res.ok) {
        toast.error(res.message || "Replacement quote not found.");
        // the list logs a silent login / SSO user out (router): back to the detail there
        this.$router.replace(isSilentLogin() ? this.detailPath : "/work-order/replacement-quote");
        return;
      }
      this.order = res.body;
    },
    openCrumb(event, to) {
      if (isNewTabClick(event)) return;
      event.preventDefault();
      goBackTo(this.$router, to);
    },
    toDetail() {
      this.$router.push(this.detailPath);
    },
  },
};
</script>
